import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import CssBaseline from "@mui/material/CssBaseline";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Link as RouterLink } from "react-router-dom/dist";
import { clearMessage } from "../features/message/messageSlice";
import ErrorAlert from "../components/alerts/ErrorAlert";
import RegisterSuccess from "../components/form/forms/registration/RegisterSuccess";
import {
  useLazyCheckTenantMailQuery,
  useRegisterTenantMutation,
} from "../features/auth/registerApiSlice";
import Copyright from "../components/copyright/Copyright";
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Link,
  Radio,
  Stack,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import Iconify from "../components/iconify/Iconify";
import LoadingScreen from "../components/LoadingScreen/LoadingScreen";
import TextField from "../components/form/fields/TextField";
import MailTextField from "../components/form/fields/MailTextField";
import PasswordTextField from "../components/form/fields/PasswordTextField";
import PasswordCheckTextField from "../components/form/fields/PasswordCheckTextField";
import IBANTextField from "../components/form/fields/IBANTextField";
import SelectField from "../components/form/fields/SelectField";
import { anredeArray } from "../components/form/fields/inputFields";
import { PLAN_IDS } from "../utils/planFeatures";
import usePlanOptions from "../hooks/usePlanOptions";

const defaultValues = {
  plan: PLAN_IDS.PREMIUM,
  firstName: "",
  lastName: "",
  mail: "",
  password: "",
  checkPassword: "",
  tenantName: "",
  title: "",
  address: "",
  zip: "",
  city: "",
  phone: "",
  accountHolder: "",
  iban: "",
  sepaConsent: false,
  agbConsent: false,
};

const STEP_LABELS = ["Tarif wählen", "Daten eingeben", "Starten"];

const StepIcons = {
  1: "mdi:tag-outline",
  2: "mdi:form-textbox",
  3: "mdi:rocket-launch-outline",
};

function StepIconComponent(props) {
  const { active, completed, className, icon } = props;
  const iconName = StepIcons[String(icon)];

  return (
    <Box
      className={className}
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: completed || active ? "secondary.main" : "grey.200",
        color: completed || active ? "common.white" : "text.secondary",
        boxShadow: active ? "0 8px 18px rgba(0,0,0,0.15)" : "none",
        transform: active ? "scale(1.05)" : "scale(1)",
        transition: "all 220ms ease",
        "@keyframes stepPulse": {
          "0%": { boxShadow: "0 0 0 0 rgba(0,0,0,0.12)" },
          "70%": { boxShadow: "0 0 0 10px rgba(0,0,0,0)" },
          "100%": { boxShadow: "0 0 0 0 rgba(0,0,0,0)" },
        },
        animation: active ? "stepPulse 1.6s ease-out infinite" : "none",
      }}
    >
      {iconName ? <Iconify icon={iconName} width={18} /> : icon}
    </Box>
  );
}

export default function RegisterPage() {
  const planOptions = usePlanOptions();
  const {
    handleSubmit,
    control,
    watch,
    setError,
    clearErrors,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues,
  });
  const [loading, setLoading] = React.useState(false);
  const [registered, setRegistered] = React.useState(false);
  const [step, setStep] = React.useState(0);
  const [confirmData, setConfirmData] = React.useState(null);
  const [sepaDialogOpen, setSepaDialogOpen] = React.useState(false);
  const [submitFeedback, setSubmitFeedback] = React.useState(null);
  const dispatch = useDispatch();

  const [registerTenant, { isLoading, error }] = useRegisterTenantMutation();
  const [checkTenantMail] = useLazyCheckTenantMailQuery();

  const { isLoggedIn } = useSelector((state) => state.auth);

  React.useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const handleCheckMatch = (checkPwd) => {
    if (watch("password") !== checkPwd) {
      return "Die Passwörter stimmen nicht überein";
    }
    return true;
  };

  const mailValue = watch("mail");
  const lastCheckedMailRef = React.useRef("");

  const isEmailFormatValid = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleRegister = async (data) => {
    delete data.checkPassword;
    // setLoading(false);

    // return console.log("data :>> ", data);

    try {
      setSubmitFeedback(null);
      const userData = await registerTenant(data).unwrap();

      if (userData && userData.status === "SUCCESS") {
        // Tenant registered successfully
        setRegistered(true);
        setLoading(false);
      }
    } catch (registerError) {
      console.log("error :>> ", registerError);
      if (registerError?.data?.value) {
        setError(registerError?.data?.value, {
          type: "custom",
          message: registerError?.data?.message,
        });
      }

      const status = registerError?.originalStatus || registerError?.status;
      if (status === 409) {
        setSubmitFeedback({
          severity: "warning",
          title: "Registrierung nicht möglich",
          message:
            "Die angegebenen Daten sind bereits vergeben. Bitte prüfen Sie Ihre Eingaben.",
        });
      } else if (!status) {
        setSubmitFeedback({
          severity: "error",
          title: "Keine Serververbindung",
          message:
            "Die Registrierung konnte nicht gesendet werden. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.",
        });
      } else {
        setSubmitFeedback({
          severity: "error",
          title: "Registrierung fehlgeschlagen",
          message:
            registerError?.data?.message ||
            "Die Registrierung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.",
        });
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data) => {
    setLoading(true);
    handleRegister(data);
  };

  const stepFields = [
    ["plan"],
    [
      "mail",
      "password",
      "checkPassword",
      "tenantName",
      "title",
      "firstName",
      "lastName",
      "phone",
      "address",
      "zip",
      "city",
      "accountHolder",
      "iban",
      "sepaConsent",
      "agbConsent",
    ],
  ];

  React.useEffect(() => {
    const normalizedMail = String(mailValue || "")
      .trim()
      .toLowerCase();
    if (!normalizedMail) {
      if (errors.mail?.type === "duplicate") {
        clearErrors("mail");
      }
      return;
    }

    if (!isEmailFormatValid(normalizedMail)) return;

    const handler = setTimeout(async () => {
      lastCheckedMailRef.current = normalizedMail;
      try {
        const result = await checkTenantMail(normalizedMail).unwrap();
        if (lastCheckedMailRef.current !== normalizedMail) return;

        if (result?.available === false) {
          setError("mail", {
            type: "duplicate",
            message: "E-Mail Adresse ist bereits in Benutzung.",
          });
        } else if (errors.mail?.type === "duplicate") {
          clearErrors("mail");
        }
      } catch (checkError) {
        console.log("mail check error :>> ", checkError);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [mailValue, checkTenantMail, setError, clearErrors, errors.mail?.type]);

  const ensureMailAvailable = async () => {
    const normalizedMail = String(getValues("mail") || "")
      .trim()
      .toLowerCase();
    if (!normalizedMail || !isEmailFormatValid(normalizedMail)) return true;

    try {
      const result = await checkTenantMail(normalizedMail).unwrap();
      if (result?.available === false) {
        setError("mail", {
          type: "duplicate",
          message: "E-Mail Adresse ist bereits in Benutzung.",
        });
        return false;
      }

      if (errors.mail?.type === "duplicate") {
        clearErrors("mail");
      }

      return true;
    } catch (checkError) {
      console.log("mail check error :>> ", checkError);
      return true;
    }
  };

  const handleNext = async () => {
    const fields = stepFields[step] || [];
    const isValid = await trigger(fields);
    if (!isValid) return;
    if (step === 1) {
      const isMailAvailable = await ensureMailAvailable();
      if (!isMailAvailable) return;
      setConfirmData(getValues());
    }
    setStep((current) => Math.min(current + 1, 2));
  };

  const handleBack = () => {
    setStep((current) => Math.max(current - 1, 0));
  };

  if (isLoggedIn) {
    return <Navigate to="/dashboard" />;
  }

  if (registered) {
    return <RegisterSuccess close={() => setRegistered(false)} />;
  }

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      {loading && <LoadingScreen />}
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={2}
        md={5}
        sx={{
          backgroundImage: "url(/assets/images/stone_sahbumnim.jpg)",
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) =>
            t.palette.mode === "light"
              ? t.palette.grey[50]
              : t.palette.grey[900],
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Grid
        item
        xs={12}
        sm={10}
        md={7}
        component={Paper}
        elevation={6}
        square
        sx={{ height: "100vh", overflowY: "auto" }}
      >
        <Typography variant="h3" align="center" sx={{ m: 2 }}>
          Verwaltungssoftware für Kampfsportschulen
        </Typography>
        <Box
          sx={{
            mt: 6,
            px: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pb: 6,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 720 }}>
            {/* <Box sx={{ width: "50%", minWidth: 350 }}> */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Schritt {step + 1} von 3
              </Typography>
              <Stepper activeStep={step} alternativeLabel>
                {STEP_LABELS.map((label) => (
                  <Step key={label}>
                    <StepLabel StepIconComponent={StepIconComponent}>
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <form
              onSubmit={
                step === 2 ? handleSubmit(onSubmit) : (e) => e.preventDefault()
              }
              className="form"
            >
              {step === 2 ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2.5, textAlign: "left", mb: 3 }}
                >
                  <Typography variant="h5" gutterBottom>
                    Bitte prüfen Sie Ihre Angaben
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Tarif
                  </Typography>
                  <Typography variant="body2">
                    {planOptions.find((plan) => plan.id === confirmData?.plan)
                      ?.title || "Nicht ausgewählt"}
                    {" — "}
                    {planOptions.find((plan) => plan.id === confirmData?.plan)
                      ?.price || "Kein Preis verfügbar"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Monatliche Abrechnung per SEPA-Lastschrift.
                  </Typography>
                  <Typography variant="body2">
                    Mindestvertragslaufzeit: 1 Jahr, danach automatische
                    Verlängerung um jeweils 1 Jahr.
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Zugangsdaten
                  </Typography>
                  <Typography variant="body2">
                    {[confirmData?.title, confirmData?.firstName, confirmData?.lastName]
                      .filter(Boolean)
                      .join(" ")}
                  </Typography>
                  <Typography variant="body2">{confirmData?.mail}</Typography>

                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Unternehmensdaten
                  </Typography>
                  <Typography variant="body2">
                    Firma/Schule: {confirmData?.tenantName}
                  </Typography>
                  <Typography variant="body2">
                    {confirmData?.address}, {confirmData?.zip}{" "}
                    {confirmData?.city}
                  </Typography>
                  <Typography variant="body2">
                    Telefon: {confirmData?.phone}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Bankverbindung
                  </Typography>
                  <Typography variant="body2">
                    Kontoinhaber: {confirmData?.accountHolder}
                  </Typography>
                  <Typography variant="body2">
                    IBAN: {confirmData?.iban}
                  </Typography>
                  <FormHelperText sx={{ mt: 2 }}>
                    Falls etwas nicht stimmt, gehen Sie zurück und korrigieren
                    Sie die Angaben.
                  </FormHelperText>
                </Paper>
              ) : step === 0 ? (
                <>
                  <Typography sx={{ my: 1 }} align="left" variant="h5">
                    Wählen Sie Ihren Tarif
                  </Typography>
                  <Typography
                    variant="body2"
                    align="left"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Die Tarife unterscheiden sich durch enthaltene Funktionen
                    und Kapazitäten.
                  </Typography>
                  <Controller
                    name="plan"
                    control={control}
                    rules={{ required: "Bitte einen Tarif auswählen." }}
                    render={({ field, fieldState }) => (
                      <Box sx={{ textAlign: "left", mb: 2 }}>
                        <Grid container spacing={2}>
                          {planOptions.map((plan) => {
                            const selected = field.value === plan.id;
                            const isRecommended = plan.id === PLAN_IDS.PREMIUM;

                            return (
                              <Grid
                                item
                                xs={12}
                                md={4}
                                key={plan.id}
                                sx={{ mt: 1, mb: 6 }}
                              >
                                <Box
                                  sx={{
                                    bgcolor: isRecommended
                                      ? "warning.main"
                                      : "transparent ",
                                    p: 0.5,
                                    textAlign: "center",
                                    fontWeight: 700,
                                  }}
                                  style={{
                                    borderTopLeftRadius: 8,
                                    borderTopRightRadius: 8,
                                  }}
                                >
                                  {plan.badge ?? <span>&nbsp;</span>}
                                </Box>
                                <Card
                                  onClick={() => field.onChange(plan.id)}
                                  style={{
                                    borderTopLeftRadius: isRecommended ? 0 : 12,
                                    borderTopRightRadius: isRecommended
                                      ? 0
                                      : 12,
                                  }}
                                  sx={{
                                    cursor: "pointer",
                                    border: "2px solid",
                                    borderColor: selected
                                      ? "primary.main"
                                      : isRecommended
                                      ? "warning.main"
                                      : "divider",
                                    bgcolor: selected
                                      ? "rgba(25, 118, 210, 0.06)"
                                      : "background.paper",
                                    boxShadow: selected ? 10 : 2,
                                    height: "100%",
                                    transition:
                                      "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                                    "&:hover": {
                                      transform: "translateY(-4px)",
                                      boxShadow: 10,
                                    },
                                  }}
                                >
                                  <CardContent sx={{ p: 2 }}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      sx={{ mb: 1 }}
                                    >
                                      <Typography variant="h6">
                                        {plan.title}
                                      </Typography>
                                      <Radio
                                        checked={selected}
                                        onChange={() => field.onChange(plan.id)}
                                        value={plan.id}
                                      />
                                    </Stack>
                                    <Typography
                                      variant="body2"
                                      sx={{ mb: 0.5 }}
                                    >
                                      {plan.limits}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ mb: 1.5 }}
                                    >
                                      {plan.members}
                                    </Typography>
                                    <Typography variant="h4" sx={{ mb: 1.5 }}>
                                      {plan.price}
                                    </Typography>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Stack spacing={0.75}>
                                      {plan.includedFeatures.map(
                                        (feature, i) => (
                                          <Stack
                                            key={`${plan.id}-${feature}`}
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                          >
                                            <Iconify
                                              icon="eva:checkmark-circle-2-fill"
                                              width={16}
                                              sx={{
                                                color:
                                                  i === 0 &&
                                                  plan.id !== PLAN_IDS.BASIS
                                                    ? "primary.main"
                                                    : "success.main",
                                              }}
                                            />
                                            <Typography
                                              variant="body2"
                                              fontWeight={
                                                i === 0 &&
                                                plan.id !== PLAN_IDS.BASIS
                                                  ? 600
                                                  : 400
                                              }
                                            >
                                              {feature}
                                            </Typography>
                                          </Stack>
                                        ),
                                      )}
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                        {fieldState.error?.message && (
                          <FormHelperText error sx={{ mt: 1 }}>
                            {fieldState.error.message}
                          </FormHelperText>
                        )}
                      </Box>
                    )}
                  />
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mt: 2,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, rgba(255,248,225,0.6), rgba(255,255,255,1))",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Vertragsbedingungen
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      • Monatliche Abrechnung per SEPA-Lastschrift
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      • Mindestvertragslaufzeit: 1 Jahr
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      • Nach Ablauf der Mindestvertragslaufzeit monatlich
                      kündbar
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sie können später in den Einstellungen auch individuell
                      Benutzer, Mitglieder und Standorte kostenpflichtig
                      hinzubuchen.
                    </Typography>
                  </Paper>
                </>
              ) : (
                <>
                  <Typography sx={{ my: 2 }} align="left" variant="h5">
                    Zugangsdaten
                  </Typography>
                  <Grid spacing={3} container>
                    <Grid item xs={12}>
                      <MailTextField fullWidth control={control} />
                      <FormHelperText style={{ textAlign: "left" }}>
                        Diese E-Mail ist Ihr Login und kann für Systemmeldungen
                        verwendet werden.
                      </FormHelperText>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <PasswordTextField fullWidth control={control} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <PasswordCheckTextField
                        fullWidth
                        handleCheckMatch={handleCheckMatch}
                        name="checkPassword"
                        label="Passwort wiederholen"
                        control={control}
                      />
                    </Grid>
                  </Grid>

                  <Divider orientation="horizontal" sx={{ m: 3 }} flexItem />
                  <Typography sx={{ my: 2 }} align="left" variant="h5">
                    Meine Daten
                  </Typography>
                  <Typography align="left" variant="body2" sx={{ mb: 2 }}>
                    Diese Daten werden auch als Rechnungsadresse verwendet.
                  </Typography>
                  <Grid spacing={3} container>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="tenantName"
                        label="Firmenname"
                        errorMsg="Bitte angeben"
                        required="Bitte angeben"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SelectField
                        style={{ minWidth: 180, width: "100%" }}
                        control={control}
                        name="title"
                        label="Anrede"
                        required
                        errorMsg="Bitte eine Anrede auswählen."
                        itemArray={anredeArray}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="firstName"
                        label="Vorname"
                        errorMsg="Bitte angeben"
                        required="Bitte angeben"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="lastName"
                        label="Nachname"
                        errorMsg="Bitte angeben"
                        required="Bitte angeben"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="phone"
                        label="Telefon / Mobil"
                        errorMsg="Bitte angeben"
                        required="Bitte angeben"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="address"
                        label="Straße Nr."
                        errorMsg="Bitte angeben"
                        required="Bitte angeben"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="zip"
                        label="PLZ"
                        errorMsg="Bitte angeben"
                        required="Bitte angeben"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        name="city"
                        label="Stadt"
                        errorMsg="Bitte angeben"
                        required="Bitte angeben"
                        control={control}
                      />
                    </Grid>
                  </Grid>

                  <Divider orientation="horizontal" sx={{ m: 3 }} flexItem />
                  <Typography sx={{ my: 2 }} align="left" variant="h5">
                    Bankverbindung
                  </Typography>
                  <Grid spacing={3} container>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="accountHolder"
                        label="Kontoinhaber"
                        errorMsg="Bitte angeben"
                        required="Bitte angeben"
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IBANTextField
                        fullWidth
                        name="iban"
                        label="IBAN"
                        required="Bitte eine IBAN eingeben."
                        control={control}
                        setError={setError}
                        clearErrors={clearErrors}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="sepaConsent"
                        control={control}
                        rules={{
                          validate: (value) =>
                            value ||
                            "Bitte dem SEPA-Lastschriftverfahren zustimmen.",
                        }}
                        render={({ field, fieldState }) => (
                          <Box textAlign="left">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.value}
                                  onChange={(e) =>
                                    field.onChange(e.target.checked)
                                  }
                                />
                              }
                              label="Ich stimme dem SEPA-Lastschriftverfahren zu."
                            />
                            <Button
                              type="button"
                              size="small"
                              onClick={() => setSepaDialogOpen(true)}
                              sx={{ ml: 1, textTransform: "none" }}
                            >
                              SEPA-Bedingungen ansehen
                            </Button>
                            {fieldState.error?.message && (
                              <FormHelperText error>
                                {fieldState.error.message}
                              </FormHelperText>
                            )}
                          </Box>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="agbConsent"
                        control={control}
                        rules={{
                          validate: (value) =>
                            value || "Bitte den AGB und der AVV zustimmen.",
                        }}
                        render={({ field, fieldState }) => (
                          <Box textAlign="left">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.value}
                                  onChange={(e) =>
                                    field.onChange(e.target.checked)
                                  }
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  Ich akzeptiere die{" "}
                                  <Link
                                    component={RouterLink}
                                    to="/agb"
                                    target="_blank"
                                  >
                                    AGB
                                  </Link>{" "}
                                  und die{" "}
                                  <Link
                                    component={RouterLink}
                                    to="/avv"
                                    target="_blank"
                                  >
                                    Vereinbarung zur Auftragsverarbeitung (AVV)
                                  </Link>
                                  .
                                </Typography>
                              }
                            />
                            {fieldState.error?.message && (
                              <FormHelperText error>
                                {fieldState.error.message}
                              </FormHelperText>
                            )}
                          </Box>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <FormHelperText style={{ textAlign: "left", marginTop: 8 }}>
                    Hinweis: Weitere Details können später im Dashboard ergänzt
                    werden.
                  </FormHelperText>
                </>
              )}
              {submitFeedback && (
                <Alert
                  severity={submitFeedback.severity}
                  variant="filled"
                  sx={{ mt: 2, textAlign: "left", borderRadius: 2 }}
                >
                  <AlertTitle sx={{ mb: 0.5 }}>
                    {submitFeedback.title}
                  </AlertTitle>
                  {submitFeedback.message}
                </Alert>
              )}
              {error && !submitFeedback && (
                <ErrorAlert
                  errorMessage={
                    error?.data?.message ||
                    "Die Registrierung konnte nicht abgeschlossen werden."
                  }
                />
              )}

              <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
                {step > 0 && (
                  <Grid item xs={12} md={6}>
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      onClick={handleBack}
                    >
                      Zurück
                    </Button>
                  </Grid>
                )}
                {step < 2 ? (
                  <Grid item xs={12} md={step > 0 ? 6 : 12}>
                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      color="secondary"
                      disabled={isLoading || loading}
                      sx={{ fontSize: "125%" }}
                      onClick={handleNext}
                    >
                      Weiter
                    </Button>
                  </Grid>
                ) : (
                  <Grid item xs={12} md={6}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="secondary"
                      disabled={
                        isLoading ||
                        loading ||
                        !confirmData?.iban ||
                        Boolean(errors.iban)
                      }
                      sx={{ fontSize: "125%" }}
                    >
                      Kostenpflichtig registrieren
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>

            <Copyright sx={{ mt: 3 }} />
          </Box>
        </Box>
      </Grid>
      <Dialog open={sepaDialogOpen} onClose={() => setSepaDialogOpen(false)}>
        <DialogTitle>SEPA-Lastschriftverfahren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Mit Ihrer Zustimmung ermächtigen Sie uns, Zahlungen von Ihrem Konto
            per SEPA-Lastschrift einzuziehen. Gleichzeitig weisen Sie Ihr
            Kreditinstitut an, die von uns gezogenen SEPA-Lastschriften
            einzulösen.
          </Typography>
          <Typography variant="body2" paragraph>
            Sie können innerhalb von acht Wochen, beginnend mit dem
            Belastungsdatum, die Erstattung des belasteten Betrags verlangen. Es
            gelten dabei die mit Ihrem Kreditinstitut vereinbarten Bedingungen.
          </Typography>
          <Typography variant="body2">
            Bei unrechtmäßigem Lastschriftwiderspruch, unzureichender
            Kontodeckung oder anderen Rücklasten werden dem Kontoinhaber 20,00 €
            in Rechnung gestellt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSepaDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
